import { motion } from "motion/react"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router"

import Form from "@forento/ui/components/Form"

import { SubmitButton } from "~/components/Button"
import GoogleSigninButton from "~/components/GoogleSigninButton"
import InputField from "~/components/InputField"
import Link from "~/components/Link"
import { useAlert } from "~/contexts/AlertContext"
import { useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc, { getInputErrors } from "~/utilities/trpc"

import Layout, { Logo } from "./Layout"
import { authCardClass, staggerContainer, staggerItem } from "./Layout/authCard"

const SigninPage: React.FC = () => {
	const alert = useAlert()
	const user = useUser()
	const navigate = useNavigate()

	const [isSubmitting, setSubmitting] = useState(false)
	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")

	const nextUrl = useSearchParams()[0].get("next")

	const handleLogin = async () => {
		if (email.trim().length === 0 || password.length === 0) return

		try {
			setSubmitting(true)
			const result = await trpc.user.loginPassword.mutate({ email: email.trim(), password })

			if (result.status !== "success") {
				await alert.show("Login failed", "Invalid login credentials.")
				setSubmitting(false)
				return
			}

			await user.reload({ clearSessionCache: true })

			navigate(nextUrl ?? routes.main.index())
		} catch (error) {
			setSubmitting(false)
			const errors = getInputErrors(error)
			if ("email" in errors) {
				await alert.show("Login failed", "Invalid email address.")
				return
			}
			console.error(error)
		}
		setSubmitting(false)
	}

	return (
		<Layout>
			<Logo />
			<Form onSubmit={handleLogin} className={authCardClass}>
				<motion.div
					className="flex w-full flex-col items-stretch gap-0"
					variants={staggerContainer}
					initial="hidden"
					animate="visible"
				>
					<motion.div variants={staggerItem}>
						<h1 className="text-left text-xl font-bold tracking-tight text-zinc-900">
							Log in to your account
						</h1>
						<p className="mt-1.5 text-left text-sm text-zinc-500">
							Welcome back! Please enter your details.
						</p>
					</motion.div>

					<motion.div variants={staggerItem} className="mt-6">
						<InputField
							inputType="email"
							label="Email"
							value={email}
							onChange={setEmail}
							placeholder="Enter your email"
							autoComplete="email"
						/>
					</motion.div>

					<motion.div variants={staggerItem} className="mt-4">
						<InputField
							inputType="password"
							label="Password"
							value={password}
							onChange={setPassword}
							placeholder="Enter your password"
							autoComplete="current-password"
						/>
					</motion.div>

					<motion.div variants={staggerItem} className="mt-4">
						<Link
							to={routes.account.resetPassword()}
							className="text-primary cursor-pointer text-sm font-medium hover:underline"
						>
							Forgot password
						</Link>
					</motion.div>

					<motion.div variants={staggerItem} className="mt-5">
						<SubmitButton
							variant="primary"
							isLoading={isSubmitting}
							className="w-full cursor-pointer rounded-xl py-3 text-sm font-semibold"
						>
							Sign in
						</SubmitButton>
					</motion.div>

					<motion.div variants={staggerItem} className="flex items-center gap-3 py-4">
						<div className="h-px flex-1 bg-zinc-200" />
						<span className="text-xs text-zinc-400">or</span>
						<div className="h-px flex-1 bg-zinc-200" />
					</motion.div>

					<motion.div variants={staggerItem} className="flex justify-center">
						<GoogleSigninButton text="signin" />
					</motion.div>

					<motion.p variants={staggerItem} className="mt-5 text-center text-sm text-zinc-500">
						Don&apos;t have an account?{" "}
						<Link
							to={
								nextUrl
									? `${routes.account.signup()}?next=${encodeURIComponent(nextUrl)}`
									: routes.account.signup()
							}
							className="text-primary hover:text-primary/80 font-medium transition-colors"
						>
							Sign up
						</Link>
					</motion.p>
				</motion.div>
			</Form>
		</Layout>
	)
}

export default SigninPage
